import { ArrowRight, IconProps, Open } from '@velocity-exchange/icons';
import { cj, cm } from 'src/utils/classNames';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';
import { ComponentType } from 'react';
import NextLink, { LinkProps } from 'next/link';

interface Props extends WithChildren, WithClassName, LinkProps {
	/**
	 * These variants essentially mirror those of `TextButton`.
	 * Defaults to tertiary, which applies default link colors defined in `index.css`.
	 */
	variant?: 'primary' | 'secondary' | 'tertiary';
	openInNewTab?: boolean;
	leftIcon?: ComponentType<IconProps>;
	/**
	 * If not supplied, either `Open` or `ArrowRight` is used based on `openInNewTab`.
	 */
	rightIcon?: ComponentType<IconProps> | 'none';
	onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
	dataTestId?: string;
}

export default function Link({
	variant = 'tertiary',
	href,
	className,
	children,
	openInNewTab,
	rightIcon: RightIconFromProps,
	leftIcon: LeftIcon,
	onClick,
	dataTestId,
}: Props) {
	const RightIcon = (() => {
		if (!RightIconFromProps) {
			return openInNewTab ? Open : ArrowRight;
		}

		if (RightIconFromProps === 'none') {
			return null;
		}

		return RightIconFromProps;
	})();

	const variantClassName = {
		primary: cj('text-text-default hover:text-text-secondary'),
		secondary: cj('text-text-secondary hover:text-text-default'),
		// Tertiary simply uses the default link colors defined in `index.css`.
		tertiary: undefined,
	}[variant];

	return (
		<NextLink
			className={cm(
				'inline-flex w-fit items-center gap-x-xs',
				variantClassName,
				className
			)}
			target={openInNewTab ? '_blank' : undefined}
			rel={openInNewTab ? 'noopener noreferrer' : undefined}
			href={href}
			onClick={onClick}
			data-testid={dataTestId}
		>
			{/* `shrink-0` on icons to ensure they don't shrink in WebKit. */}
			{LeftIcon && <LeftIcon className="shrink-0" autoSize />}
			{children}
			{RightIcon && <RightIcon className="shrink-0" autoSize />}
		</NextLink>
	);
}
